import { ConnectorStatus } from "@/components/connectors/ConnectorStatus";
import { connectorLoadState } from "@/stores/connectors/loading";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { ActionDetails } from "@/components/connectors/ActionDetails";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { AuthPageHeader, AuthPageLayout } from "@/components/auth/AuthPageLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  connectorOrigin,
  decideConnectorAction,
  loadConnectorAction,
} from "@/stores/connectors/repository";

function useConnectorAction(id: string) {
  const { userId, userProfile } = useAuth();
  const [now, setNow] = useState(Date.now);
  const query = useQuery({
    queryKey: ["connector-action", userId, id],
    queryFn: ({ signal }) => loadConnectorAction(id, signal),
    enabled: Boolean(connectorOrigin() && id),
    retry: false,
    gcTime: 0,
    refetchInterval: (query) => (query.state.data?.state === "approved" ? 2000 : false),
  });
  const action = query.data;
  const mutation = useMutation({
    mutationFn: (accept: boolean) => {
      if (!action) throw new Error("Action unavailable");
      return decideConnectorAction(id, action.digest, accept);
    },
    onSuccess: () => query.refetch(),
    onError: () => query.refetch(),
  });
  useEffect(() => {
    if (!action) return;
    const remaining = Date.parse(action.expires_at) - Date.now();
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [action]);

  const expired = action ? Date.parse(action.expires_at) <= now : false;
  return { userProfile, query, action, mutation, expired };
}

function ReviewAction({ id }: { id: string }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const model = useConnectorAction(id);
  const { query, action, mutation } = model;
  const loadState = connectorLoadState(Boolean(connectorOrigin()), query);
  if (loadState || !action)
    return <ConnectorStatus state={loadState ?? "failed"} retry={() => void query.refetch()} />;

  return (
    <div className="space-y-6">
      <ActionIdentity action={action} userProfile={model.userProfile} />
      <p className="text-sm text-muted-foreground">{copy.clientNotice}</p>
      <section aria-label={copy.actionPreview} className="space-y-3">
        <h2 className="font-semibold">{copy.actionPreview}</h2>
        <ActionDetails value={action.preview} />
      </section>
      <details className="rounded-md border p-4">
        <summary className="cursor-pointer rounded-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {copy.actionParameters}
        </summary>
        <div className="mt-4">
          <ActionDetails value={action.arguments} />
        </div>
      </details>
      <p className="text-sm text-muted-foreground">{copy.actionNotice}</p>
      {mutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>{copy.failed}</AlertDescription>
        </Alert>
      )}
      <ActionDecision model={model} action={action} />
      <Link
        to="/settings?section=connections"
        className="block rounded-sm text-sm text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copy.settings}
      </Link>
    </div>
  );
}

type ActionModel = ReturnType<typeof useConnectorAction>;
type LoadedAction = NonNullable<ActionModel["action"]>;

function ActionIdentity({
  action,
  userProfile,
}: {
  action: LoadedAction;
  userProfile: ActionModel["userProfile"];
}) {
  const { t } = useLanguage();
  const copy = t.connectors;
  return (
    <dl className="space-y-3 break-words text-sm">
      <div>
        <dt className="text-muted-foreground">{copy.account}</dt>
        <dd>{userProfile?.email}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">{copy.client}</dt>
        <dd className="font-medium">{action.client_name || copy.unnamed}</dd>
        <dd className="break-all text-xs">{action.client_id}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">{copy.organization}</dt>
        <dd>{action.organization_name || action.organization}</dd>
      </div>
    </dl>
  );
}

function actionState(action: LoadedAction, expired: boolean) {
  const state =
    expired && (action.state === "pending" || action.state === "approved")
      ? "expired"
      : action.state;
  return state;
}

function ActionDecision({ model, action }: { model: ActionModel; action: LoadedAction }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { mutation, expired } = model;
  const state = actionState(action, expired);
  return (
    <>
      {state === "pending" ? (
        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <Button
            className="min-h-11 flex-1"
            disabled={mutation.isPending || expired}
            onClick={() => mutation.mutate(true)}
          >
            {mutation.isPending ? copy.pending : copy.approveAction}
          </Button>
          <Button
            variant="outline"
            className="min-h-11 flex-1"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(false)}
          >
            {copy.deny}
          </Button>
        </div>
      ) : (
        <p role="status" className="rounded-md bg-muted p-4">
          {copy.actionStates[state]}
        </p>
      )}
    </>
  );
}

export default function ConnectorAction() {
  const { isAuthenticated, isLoading, userId } = useAuth();
  const { t } = useLanguage();
  const { id = "" } = useParams();
  const location = useLocation();
  if (!isLoading && !isAuthenticated)
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  return (
    <AuthPageLayout contentClassName="max-w-2xl">
      <AuthPageHeader
        title={t.connectors.actionTitle}
        description={t.connectors.actionDescription}
        icon={<ShieldCheck aria-hidden="true" />}
      />
      {isLoading ? (
        <p role="status">{t.connectors.loading}</p>
      ) : (
        <ReviewAction key={`${userId}:${id}`} id={id} />
      )}
    </AuthPageLayout>
  );
}
